import { extendTheme } from '@mui/material/styles';
import { palette, darkPalette } from './palette';
import { createTypographyForScaleFactor } from './createTypographyForScaleFactor';
import { MuiAlert } from './componentOverrides/MuiAlert';
import { MuiAvatar } from './componentOverrides/MuiAvatar';
import { MuiBackdrop } from './componentOverrides/MuiBackdrop';
import { getMuiBadge } from './componentOverrides/MuiBadge';
import { getMuiButton } from './componentOverrides/MuiButton';
import { MuiButtonBase } from './componentOverrides/MuiButtonBase';
import { MuiCheckbox } from './componentOverrides/MuiCheckbox';
import { getMuiChip, type WatchdutyTheme } from './componentOverrides/MuiChip';
import { MuiDialog } from './componentOverrides/MuiDialog';
import { MuiDialogContentText } from './componentOverrides/MuiDialogContentText';
import { MuiFormControl } from './componentOverrides/MuiFormControl';
import { MuiFormControlLabel } from './componentOverrides/MuiFormControlLabel';
import { MuiFormHelperText } from './componentOverrides/MuiFormHelperText';
import { MuiLink } from './componentOverrides/MuiLink';
import { MuiListSubheader } from './componentOverrides/MuiListSubheader';
import { MuiMenuItem } from './componentOverrides/MuiMenuItem';
import { getMuiIconButton } from './componentOverrides/MuiIconButton';
import { MuiSelect } from './componentOverrides/MuiSelect';
import { MuiSvgIcon } from './componentOverrides/MuiSvgIcon';
import { MuiSwitch } from './componentOverrides/MuiSwitch';
import { getMuiTab } from './componentOverrides/MuiTab';
import { MuiTabs } from './componentOverrides/MuiTabs';
import { getMuiTextField } from './componentOverrides/MuiTextField';
import { MuiToggleButton } from './componentOverrides/MuiToggleButton';
import { MuiToggleButtonGroup } from './componentOverrides/MuiToggleButtonGroup';
import { getMuiTooltip } from './componentOverrides/MuiTooltip';
import { MuiTypography } from './componentOverrides/MuiTypography';
import { getMuiListItemText } from './componentOverrides/MuiListItemText';
import { MuiCssBaseline } from './componentOverrides/MuiCssBaseline';

// Base theme options shared between the temporary theme and final theme
const baseThemeOptions = {
  cssVarPrefix: 'mui',
  colorSchemeSelector: 'data' as const, // Use data attribute selector to enable manual mode toggling
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      laptop: 1024,
      tablet: 768,
      phone: 480,
    },
  },
  shape: {
    borderRadius: 6,
  },
  typography: createTypographyForScaleFactor(1),
  colorSchemes: {
    light: {
      palette,
    },
    dark: {
      palette: darkPalette,
    },
  },
};

// Create a temporary theme for component overrides that need theme context
const baseTheme = extendTheme({ ...baseThemeOptions });

// Create theme with ColorScheme support in a single call
export const watchdutyTheme = extendTheme({
  ...baseThemeOptions,
  components: {
    MuiCssBaseline,
    MuiFormControl,
    MuiFormControlLabel,
    MuiFormHelperText,
    MuiMenuItem,
    MuiCheckbox,
    MuiDialog,
    MuiDialogContentText,
    MuiSwitch,
    MuiButton: getMuiButton(baseTheme),
    MuiIconButton: getMuiIconButton(baseTheme),
    MuiToggleButton,
    MuiToggleButtonGroup,
    MuiTextField: getMuiTextField(baseTheme as WatchdutyTheme),
    MuiSelect,
    MuiAlert,
    MuiAvatar,
    MuiBackdrop,
    MuiBadge: getMuiBadge(baseTheme),
    MuiSvgIcon,
    MuiChip: getMuiChip(baseTheme as WatchdutyTheme),
    MuiTabs,
    MuiTab: getMuiTab(baseTheme),
    MuiTooltip: getMuiTooltip(baseTheme),
    MuiTypography,
    MuiListItemText: getMuiListItemText(baseTheme),
    MuiListSubheader,
    MuiLink,
    MuiButtonBase,
  },
});
